import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import * as types from '../../lib/constants';
import MediaInfo from './MediaInfo';
import MediaFooter from './MediaFooter';
import MediaMap from '../Map/MediaMap';
import imgMusic from '../../images/media/ic_music.svg';
import chevronLeftImg from '../../images/common/chevron_left.svg';
import chevronRightImg from '../../images/common/chevron_right.svg';
import ColorSF2 from '../../styles/common/ColorSF2';

const Contents = styled.div`
  width: 100%;
  height: ${props => `calc(100% - ${props.mediaDetailHeight || 0}px)`};
  margin-top: 5px;
  display: flex;
  flex-direction: row;
`;
const NaviBlock = styled.div`
  width: 60px;
`;
const NaviButton = styled.div`
  width: 100%;
  height: 100%;
  border-left: 1px ${ColorSF2.white} solid;
  border-right: 1px ${ColorSF2.white} solid;
  background-color: ${ColorSF2.paleBlue};
  display: flex;
  justify-content: center;
  align-items: center;
`;
const NaviImage = styled.img`
  width: 40px;
  height: 40px;
`;
// fullscreenの場合は60×2px、それ以外は287pxをさらに引く
const MediaBox = styled.div`
  width: ${props => `calc(100% - ${props.isFullscreen ? 120 : 407}px)`};
  height: 100%;
  background-color: #000;
  display: flex;
  flex-direction: column;
`;
const MediaContent = styled.div`
  width: 100%;
  height: ${props => `calc(100% - ${props.isOpen ? 240 : 40}px)`};
`;
const Nofile = styled.div`
  width: 100%;
  height: 100%;
  border: 1px dotted #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ccc;
`;
const MediaWrapper = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
`;
const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const MediaVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const AudioWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${ColorSF2.navyBlue};
  display: flex;
  align-items: center;
  flex-direction: column;
`;
const AudioPlayer = styled.audio`
  margin-bottom: 15px;
`;
const MusicIcon = styled.div`
  width: 100%;
  height: 100%;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 180px;
`;
const DescriptionBox = styled.div`
  width: 287px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
`;
const MediaInfoWrapper = styled.div`
  flex: 1 1 0%;
  background-color: #1f1f2c;
`;
const MediaMapTitle = styled.div`
  width: 100%;
  height: 40px;
  background-color: ${ColorSF2.paleBlue};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  margin: 0px 0 1px 0;
`;
const MapWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  height: calc(100% - 40px);
`;
const MediaMapBlock = styled.div`
  flex: 1 1 100%;
  width: 100%;
  height: ${props => `calc(100% - ${props.subtrahend}px)`};
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
`;
const FullscreenMap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
`;

const MediaDetail = ({
  mediaDetailHeight,
  innerRole,
  mediaInfo,
  operators,
  mediaFullScreenMode,
  mediaMapFullScreenMode,
  isOpenAnalysis,
  isOpenMore,
  userGroups,
  onChangePage,
  onDownloadUncompressed,
  onPost,
  onUpdateTagsDialog,
  onDelete,
  onRestore,
  onCompleteDelete,
  onToggleAction,
  onToggleResult,
  onToggleMap,
  provider,
}) => {
  const mediaType = mediaInfo.mediaType || types.MEDIA_UNKNOWN;
  const mediaUrl = mediaInfo.mediaFileUrl;
  const operator =
    (operators &&
      operators.find(op => mediaInfo.operatorId === op.operatorId)) ||
    {};
  const mediaInfoObj = document.getElementById('MediaInfoBlock');
  const mediaInfoHeight = (mediaInfoObj && mediaInfoObj.offsetHeight) || 0;
  const isMediaWindowed =
    mediaFullScreenMode === types.MEDIA_FULLSCREEN_MODE.windowed;
  return (
    <Fragment key="MediaPageContents">
      {mediaMapFullScreenMode === types.MEDIA_MAP_FULLSCREEN_MODE.windowed && (
        <Contents mediaDetailHeight={mediaDetailHeight}>
          <NaviBlock>
            {mediaInfo.existsPrevData && (
              <NaviButton
                className="button"
                id="existsPrevButton"
                role="presentation"
                onClick={() => onChangePage(types.MEDIA_SEARCH_POSITION.prev)}
              >
                <NaviImage src={chevronLeftImg} alt="" />
              </NaviButton>
            )}
          </NaviBlock>
          <MediaBox
            isFullscreen={
              mediaFullScreenMode === types.MEDIA_FULLSCREEN_MODE.fullscreen
            }
          >
            <MediaContent isOpen={isOpenAnalysis}>
              {mediaType === types.MEDIA_UNKNOWN && (
                <Nofile>
                  <FormattedMessage id="media.detail.nofile" />
                </Nofile>
              )}
              {mediaType === types.IMAGE && (
                <MediaWrapper>
                  <MediaImage src={mediaUrl} alt="img" />
                </MediaWrapper>
              )}
              {mediaType === types.VIDEO && (
                <MediaWrapper>
                  <MediaVideo
                    src={mediaUrl}
                    controls
                    autoPlay
                    controlsList="nodownload"
                  >
                    <track kind="captions" />
                  </MediaVideo>
                </MediaWrapper>
              )}
              {mediaType === types.AUDIO && (
                <MediaWrapper>
                  <AudioWrapper>
                    <MusicIcon>
                      <img src={imgMusic} alt="" />
                    </MusicIcon>
                    <AudioPlayer
                      src={mediaUrl}
                      controls
                      autoPlay
                      controlsList="nodownload"
                    >
                      <track kind="captions" />
                    </AudioPlayer>
                  </AudioWrapper>
                </MediaWrapper>
              )}
            </MediaContent>
            <MediaFooter
              innerRole={innerRole}
              isOpenAnalysis={isOpenAnalysis}
              isOpenMore={isOpenMore}
              onDownloadUncompressed={onDownloadUncompressed}
              onUpdateTagsDialog={onUpdateTagsDialog}
              onDelete={onDelete}
              onRestore={onRestore}
              onCompleteDelete={onCompleteDelete}
              onPost={onPost}
              onToggleAction={onToggleAction}
              onToggleResult={onToggleResult}
              isMediaWindowed={isMediaWindowed}
              provider={provider}
            />
          </MediaBox>
          {isMediaWindowed && (
            <DescriptionBox>
              <MediaInfoWrapper id="MediaInfoBlock">
                <MediaInfo
                  mediaInfo={mediaInfo}
                  operator={operator}
                  provider={provider}
                  userGroups={userGroups}
                />
              </MediaInfoWrapper>
              {mediaType !== types.MEDIA_UNKNOWN &&
                mediaInfo.lat !== null &&
                mediaInfo.lng !== null && (
                  <MediaMapBlock subtrahend={mediaInfoHeight}>
                    <MediaMapTitle>MAP</MediaMapTitle>
                    <MapWrapper>
                      <MediaMap
                        mediaInfo={mediaInfo}
                        fullscreen={false}
                        onToggleFullScreen={onToggleMap}
                      />
                    </MapWrapper>
                  </MediaMapBlock>
                )}
            </DescriptionBox>
          )}
          <NaviBlock>
            {mediaInfo.existsNextData && (
              <NaviButton
                className="button"
                id="existsNextButton"
                role="presentation"
                onClick={() => onChangePage(types.MEDIA_SEARCH_POSITION.next)}
              >
                <NaviImage src={chevronRightImg} alt="" />
              </NaviButton>
            )}
          </NaviBlock>
        </Contents>
      )}
      {mediaMapFullScreenMode ===
        types.MEDIA_MAP_FULLSCREEN_MODE.fullscreen && (
        <FullscreenMap>
          <MediaMap
            mediaInfo={mediaInfo}
            fullscreen
            onToggleFullScreen={onToggleMap}
          />
        </FullscreenMap>
      )}
    </Fragment>
  );
};

MediaDetail.propTypes = {
  mediaDetailHeight: PropTypes.number.isRequired,
  innerRole: PropTypes.number.isRequired,
  mediaInfo: PropTypes.object.isRequired,
  operators: PropTypes.array.isRequired,
  mediaFullScreenMode: PropTypes.number.isRequired,
  mediaMapFullScreenMode: PropTypes.number.isRequired,
  isOpenAnalysis: PropTypes.bool.isRequired,
  isOpenMore: PropTypes.bool.isRequired,
  userGroups: PropTypes.array.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onDownloadUncompressed: PropTypes.func,
  onUpdateTagsDialog: PropTypes.func,
  onDelete: PropTypes.func,
  onRestore: PropTypes.func,
  onCompleteDelete: PropTypes.func,
  onPost: PropTypes.func,
  onToggleAction: PropTypes.func.isRequired,
  onToggleResult: PropTypes.func.isRequired,
  onToggleMap: PropTypes.func.isRequired,
  provider: PropTypes.object.isRequired,
};

MediaDetail.defaultProps = {
  onDownloadUncompressed: null,
  onUpdateTagsDialog: null,
  onDelete: null,
  onRestore: null,
  onCompleteDelete: null,
  onPost: null,
};

export default MediaDetail;
